import { useQueries, useQuery } from '@tanstack/react-query';
import { prop, uniq, uniqBy } from 'ramda';

import {
  GetPropertyExplorerScenesArgs,
  getPropertyExplorerScenes,
} from '../queries/getPropertyExplorerScenes';

const uniqByUuid = uniqBy(prop('uuid'));

export const usePropertyExplorerScenes = ({
  projectUuid,
  sceneUuid,
}: GetPropertyExplorerScenesArgs) => {
  const mainSceneQuery = useQuery({
    queryKey: ['property-explorer-scenes', projectUuid, sceneUuid],
    queryFn: () => getPropertyExplorerScenes({ projectUuid, sceneUuid }),
    enabled: !!projectUuid,
    staleTime: 1000 * 60 * 30,
  });

  const scenes = mainSceneQuery.data?.scenes ?? [];
  const rendererType = mainSceneQuery.data?.rendererType;

  const referencedUuids = uniq(
    scenes
      .flatMap((scene) => scene.references)
      .filter((ref) => ref?.target.type === 'view')
      .map((ref) => ref?.target.id)
      .filter((uuid) => !!uuid),
  ) as string[];

  const allSceneUuids = scenes.map(prop('uuid'));
  const missingUuids = referencedUuids.filter((uuid) => !allSceneUuids.includes(uuid));

  const referencedScenesQueries = useQueries({
    queries: missingUuids.map((uuid) => ({
      queryKey: ['property-explorer-scenes', projectUuid, uuid],
      queryFn: () => getPropertyExplorerScenes({ projectUuid, sceneUuid: uuid }),
      enabled: !!projectUuid,
      staleTime: 1000 * 60 * 30,
    })),
  });

  const referencedScenes = referencedScenesQueries
    .filter((q) => q.status === 'success')
    .flatMap((q) => q.data?.scenes ?? []);

  const allScenes = uniqByUuid([...scenes, ...referencedScenes]);

  const isLoading =
    mainSceneQuery.isLoading ||
    referencedScenesQueries.some((q) => q.isLoading || q.status === 'pending');

  const isError = mainSceneQuery.isError || referencedScenesQueries.some((q) => q.isError);

  return {
    scenes: allScenes,
    rendererType,
    isLoading,
    isError,
    refetch: mainSceneQuery.refetch,
  };
};
